import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Ellie Streaming API',
  content: `
The Ellie Streaming API is the public, streaming entry point into Ellie — built for a tenant's own integration to drive a real conversation, streamed live token by token. Use this if you're building a human-facing chat interface. It's meant to be proxied: your backend opens this stream and re-emits a translated form of it to whatever frontend you're building, rather than exposing this endpoint straight to a browser (your backend holds the bearer token; the browser never should).

If you're calling Ellie as a sub-agent from your own harness or orchestrator instead — one request in, one complete answer out, no streaming needed — see [Ellie Invoke API](/docs/ellie-invoke-api).

---

## Authenticate

Exchange your API credentials for a bearer token the same way as the invoke API — see the "Authenticate" section on [Ellie Invoke API](/docs/ellie-invoke-api). Everything below uses the same token.

---

## Start or continue a conversation

\`\`\`
POST /api/v1/ext/ellie-streaming
\`\`\`

\`\`\`bash
curl -N -X POST "https://app.pliable.co/api/v1/ext/ellie-streaming" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "What were total sales last month by region?"}'
\`\`\`

| Field | Type | Required | Description |
|---|---|---|---|
| \`message\` | string | Yes (unless resuming via \`command\`) | The user's message. |
| \`thread_id\` | string | No | Continue an existing thread, or supply a UUID of your own choosing to start a new one under a known id. Must be a real UUID if provided; omit it to have the server generate one. |
| \`model\` | string | No | LLM override; the tenant's configured Ellie default otherwise. |
| \`verbosity\` / \`reasoning\` | string | No | Same values as the rest of the platform. |
| \`command\` | object | No | \`{"resume": <value>}\` — answers a \`needs_input\` event (see below). |

**Continuing a thread only needs the new message and \`thread_id\`** — don't resend prior history. The server reconstructs the conversation from its own checkpointed state. A thread only belongs to the identity that started it — reusing someone else's \`thread_id\` is rejected.

---

## Reading the stream

Each line is \`data: {"event": ..., "data": ...}\`, terminated by a literal \`data: [DONE]\` line.

| Event | Data | Meaning |
|---|---|---|
| \`message.delta\` | \`{thread_id, delta}\` | Next chunk of the assistant's reply. Concatenate \`delta\` in order. |
| \`tool.progress\` | \`{thread_id, status: "running"}\` | Ellie is running a tool (fetching data, building an analysis). No internal detail — just enough to show a spinner. |
| \`message.done\` | \`{thread_id, response, tool_calls, analyses, artifacts}\` | The turn is finished. Same shape as the [Ellie Invoke API](/docs/ellie-invoke-api)'s response. |
| \`needs_input\` | \`{thread_id, tool_call_id, type, ...}\` | The conversation is paused, waiting on you. See below. |
| \`error\` | \`{thread_id, message}\` | Something failed; the stream ends here. |

---

## Handling \`needs_input\` (clarifying questions)

Some turns pause mid-conversation to ask the end-user something instead of guessing — e.g. "which entity did you mean?". When that happens you get a \`needs_input\` event instead of (or in addition to) further deltas, and the stream ends without a \`message.done\`:

\`\`\`json
{
  "event": "needs_input",
  "data": {
    "thread_id": "…",
    "tool_call_id": "tc_123",
    "type": "user_choice",
    "status": "awaiting_choice",
    "questions": [
      {"header": "Entity", "question": "Which entity?", "options": [{"label": "Customer"}, {"label": "Order"}], "multi_select": false}
    ]
  }
}
\`\`\`

Render the question(s)/options, then **resume the same endpoint** (not a separate one) with \`thread_id\` + \`command.resume\`:

\`\`\`bash
curl -N -X POST "https://app.pliable.co/api/v1/ext/ellie-streaming" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "thread_id": "…",
    "command": {
      "resume": {
        "status": "answered",
        "answers": [{"header": "Entity", "question": "Which entity?", "selected": ["Customer"]}]
      }
    }
  }'
\`\`\`

\`answers\` has one entry per question, matched by array position to the \`questions\` you were sent. \`selected\` is an array of the chosen option label(s) (multiple entries for a multi-select question). If your end-user would rather just type an answer instead of using your UI for the pause, that's fine too: post a normal \`message\` on the same thread instead of a \`command.resume\` — Ellie treats it as an ordinary reply and moves on.

**File uploads are not supported.** Ellie will never ask for one over this endpoint — file-backed data sources need the in-app upload flow.

---

Building a sub-agent integration instead of a chat UI? See [Ellie Invoke API](/docs/ellie-invoke-api) for the synchronous entry point, including how to watch a synchronous call live.
`,
};

export default doc;
